import { authClient } from './amplifyClient';
import { getReport as getReportQuery } from '../graphql/queries';
import { updateReport as updateReportMutation } from '../graphql/mutations';
import type { Discrepancy } from '../types/schema';
import { ITEM_STATUS_CODES, ITEM_STATUS_CONTENT } from './constants';
import { archiveDiscrepancyRecord } from './issueArchiveUtils';

export function appendStatusChangeLog(
    discrepancy: Discrepancy,
    actorName: string,
    userNotes: string,
    status: string,
): string {
    const statusLabel = ITEM_STATUS_CONTENT[status as keyof typeof ITEM_STATUS_CONTENT] ?? status;
    const priorLog = JSON.parse(discrepancy.log || '[]');

    return JSON.stringify([
        ...priorLog,
        {
            name: actorName,
            notes: `${userNotes} [Updated Status: ${statusLabel}] `,
            date: new Date().toISOString(),
        },
    ]);
}

export async function syncLinkedReportPromptStatus(discrepancy: Discrepancy, status: string): Promise<void> {
    if (
        !discrepancy.promptTitle ||
        !discrepancy.promptLabel ||
        !discrepancy.promptDescription ||
        !discrepancy.reportId
    ) {
        return;
    }

    const reportResult = await authClient.graphql({
        query: getReportQuery,
        variables: {
            id: discrepancy.reportId,
        },
    });

    const report = reportResult.data?.getReport;
    if (!report) {
        return;
    }

    const userData = JSON.parse(report.userData || '[]');
    const updatedUserData = userData.map(
        (item: { title?: string; label?: string; description?: string; value?: string }) => {
            if (
                item.title === discrepancy.promptTitle &&
                item.label === discrepancy.promptLabel &&
                item.description === discrepancy.promptDescription
            ) {
                return {
                    ...item,
                    value: status,
                };
            }
            return item;
        },
    );

    await authClient.graphql({
        query: updateReportMutation,
        variables: {
            input: {
                id: report.id,
                userData: JSON.stringify(updatedUserData),
            },
        },
    });
}

/**
 * Marks an issue Resolved, appends resolution notes to history, syncs linked report prompts, and archives.
 */
export async function resolveAndArchiveDiscrepancy(
    discrepancy: Discrepancy,
    resolutionNotes: string,
    actorName: string,
): Promise<void> {
    const trimmedNotes = resolutionNotes.trim();
    if (!trimmedNotes) {
        throw new Error('Resolution notes are required before archiving.');
    }

    const nextLog = appendStatusChangeLog(discrepancy, actorName || 'Unknown', trimmedNotes, ITEM_STATUS_CODES.ok);

    await Promise.all([
        syncLinkedReportPromptStatus(discrepancy, ITEM_STATUS_CODES.ok),
        archiveDiscrepancyRecord({
            ...discrepancy,
            status: ITEM_STATUS_CODES.ok,
            log: nextLog,
        }),
    ]);
}
